import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import StatCard from "./StatCard";
import TopologyGraph from "./TopologyGraph";
import HubHealthGrid from "./HubHealthGrid";
import StabilityTimeline, { type StabilityEvent } from "./StabilityTimeline";
import {
  adaptTopology,
  type ApiTopologyDevice,
  type ApiTopologyHub,
  type HealthView,
  type TopologyDevice,
} from "../lib/topologyAdapter";

let stabilityEventSeq = 0;

export interface HostTopologyProps {
  hostname: string;
  ipAddress: string;
  hubs: ApiTopologyHub[] | null | undefined;
  devices: ApiTopologyDevice[] | null | undefined;
}

/**
 * "USB Topology & Health" section for the Host Detail page (accepted spec
 * §5). KPI row + fan-out graph + hub health cards + a live connection
 * stability strip. Deliberately has NO power KPI/meter/callout — hardware
 * validation proved this hardware has no live power telemetry (see
 * src/api/hub-health.ts's header comment).
 */
export default function HostTopology({ hostname, ipAddress, hubs, devices }: HostTopologyProps) {
  const navigate = useNavigate();

  // adaptTopology guards null/undefined itself, but callers are expected to
  // pass `hubs ?? []` / `devices ?? []` per the accepted spec's guard rule —
  // enforced again here defensively.
  const viewModel = useMemo(
    () => adaptTopology(hubs ?? [], devices ?? []),
    [hubs, devices]
  );

  const [stabilityEvents, setStabilityEvents] = useState<StabilityEvent[]>([]);
  const prevHealthRef = useRef<Map<string, HealthView>>(new Map());
  const hubHealthFingerprint = viewModel.hubs.map((h) => `${h.id}:${h.health}`).join("|");

  // Track hub health *transitions* live, client-side, for the stability
  // strip. There's no backend endpoint for a persisted health-change
  // history — the API only reports each hub's current health — so this
  // intentionally only covers the current browser session (see
  // StabilityTimeline's doc comment).
  useEffect(() => {
    const prev = prevHealthRef.current;
    const next = new Map<string, HealthView>();
    const newEvents: StabilityEvent[] = [];

    for (const hub of viewModel.hubs) {
      next.set(hub.id, hub.health);
      const prevHealth = prev.get(hub.id);
      if (prevHealth !== undefined && prevHealth !== hub.health) {
        let kind: StabilityEvent["kind"] = "degraded";
        if (hub.health === "good") kind = "recovered";
        else if (hub.health === "unknown") kind = "stale";
        newEvents.push({
          id: `${hub.id}-${Date.now()}-${stabilityEventSeq++}`,
          atMs: Date.now(),
          hubName: hub.name,
          kind,
        });
      }
    }

    prevHealthRef.current = next;
    if (newEvents.length > 0) {
      setStabilityEvents((cur) => [...cur, ...newEvents].slice(-200));
    }
    // Only re-run when a hub's health actually changes — fingerprint string
    // is the intentional dependency, not viewModel.hubs itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubHealthFingerprint]);

  const handleSelectDevice = (device: TopologyDevice) => {
    navigate(`/devices/${device.id}`);
  };

  const { kpis } = viewModel;

  return (
    <div style={{ marginBottom: "var(--space-xl)" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "var(--space-lg)",
          marginBottom: "var(--space-lg)",
        }}
      >
        <StatCard label="Devices" value={kpis.deviceCount} color="var(--color-info)" note="across USB hub trees" />
        <StatCard label="Online" value={kpis.onlineCount} color="var(--color-success)" note="ready to claim" />
        <StatCard
          label="Offline"
          value={kpis.offlineCount}
          color="var(--color-danger)"
          note={kpis.offlineCount > 0 ? "no network" : "none"}
        />
        <StatCard label="Hubs" value={kpis.hubCount} color="var(--color-info)" note="top-level USB hubs" />
        <StatCard
          label="Degraded links"
          value={kpis.degradedLinkCount}
          color="var(--color-danger)"
          note="below high-speed"
        />
      </div>

      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          padding: "var(--space-lg)",
          marginBottom: "var(--space-lg)",
        }}
      >
        <p style={labelStyle}>USB Topology — host → hubs → devices</p>
        <TopologyGraph
          hostname={hostname}
          ipAddress={ipAddress}
          hubs={viewModel.hubs}
          devices={viewModel.devices}
          onSelectDevice={handleSelectDevice}
        />
      </div>

      <p style={labelStyle}>Hub health</p>
      <div style={{ marginBottom: "var(--space-lg)" }}>
        <HubHealthGrid hubs={viewModel.hubs} devices={viewModel.devices} />
      </div>

      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          padding: "var(--space-lg)",
        }}
      >
        <p style={labelStyle}>Connection stability (this session)</p>
        <StabilityTimeline events={stabilityEvents} />
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  textTransform: "uppercase",
  fontSize: "var(--font-size-xs)",
  letterSpacing: "0.5px",
  color: "var(--color-text-secondary)",
  fontWeight: 600,
  marginBottom: "var(--space-sm)",
};
